/**
 * AppShell — the application shell.
 *
 *   [logo]                  ← floats at the window's top-left corner
 *   [ChatDrawer] [NavRail] [PanelStage]
 *
 * Panel buttons live in a vertical, resizable rail. AI chat is opened and
 * closed by the logo and renders as a drawer to the left of the rail.
 *
 * A phone has room for none of that beside a panel: the stage is the whole
 * screen, the rail's list takes it whenever no panel is open (PhoneNav), and AI
 * chat is a panel on the stage like any other.
 *
 * PageTheme puts `theme-occurra` and, for the dark theme, `dark` on the page
 * root. Panels with `themes: 'dark'` add their own `dark` class locally so
 * they ignore the global toggle (e.g. camera view).
 */

import type { Getter, Setter } from 'jotai';
import { useAtomValueRawSync, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ContractViolationBanner } from '../ContractViolationBanner';
import { navigationGestures } from '../navigationGestures';
import { isDeferred, requestPanelClose, requestPanelDeparture } from '../panelCloseGuards';
import { isPanelId } from '../panelIds';
import { usePublishOpenPanels } from '../panelPresence';
import { PanelStage } from '../PanelStage';
import { PortalUnavailableBanner } from '../PortalUnavailableBanner';
import { listPanels, panels as registry } from '../registry';
import { shellCameraPick } from '../shellCameraPick';
import { ShellControlContext } from '../ShellControlContext';
import { beginStageAnimation } from '../stageMotion';
import { useStageLayout } from '../useStageLayout';
import { projectViewState } from '../viewState';
import { TooltipProvider } from '@/components/shadcn/tooltip';
import { useLatest } from '@/hooks/useLatest';
import { useVisualViewportBox } from '@/hooks/useVisualViewportBox';
import { installLongPressContextMenu } from '@/lib/longPressContextMenu';
import { cn } from '@/lib/utils';
import { planCameraPick } from '@/state/ui/cameraPickPlan';
import {
    clearShellSessionAtom, closePanelAtom, closeStagePanelsAtom, focusPanelAtom, modeAtom,
    normalizeShellUrlAtom, openIdsAtom, openPanelAtom, openPanelsAtom, openPlannerAtom,
    openRestoredAutoChildAtom, panelRevealedByClosingAtom, setPanelPropAtom,
    setPanelPropsAtom, stepBackAtom, visibleIdsAtom,
} from '@/state/ui/shellPanelsAtoms';
import { focusedIdAtom } from '@/state/ui/shellRecordAtoms';
import { isSmViewportAtom } from '@/state/ui/shellStageAtoms';
import { themeAtom } from '@/state/ui/themeAtoms';
import type {
    CameraPickAction, CameraSelection, DeferredDeparture, OpenPanelOptions, OpenPlanner,
    PanelHostBindings, PanelId, PanelOpenRequest, PanelSummary, ShellControl, ViewState,
} from '@/types/panels';

import { CHAT_PANEL_ID, ChatDrawer } from './ChatDrawer';
import { HomeLauncher } from './HomeLauncher';
import { Logo } from './Logo';
import { NavRail } from './NavRail';
import { NavResizeHandle } from './NavResizeHandle';
import { PhoneNav } from './PhoneNav';
import { CHAT_SLIDE_WINDOW_MS } from './slideMotion';
import { useChatSlide } from './useChatSlide';
import { useHistoryFocus } from './useHistoryFocus';
import { useHistoryGuard } from './useHistoryGuard';
import { useUpdateOnOpen } from './useUpdateOnOpen';

const VIEW_PANEL: PanelId = 'camera_view';

// Tabs whose UX is built around the live preview. Pulled from camera_view's
// `previewTabs` so the "preview matters here" set is authored in exactly one
// place (panel.config.js). These tabs auto-open the preview on tab transition;
// they do not change panel width — switching tabs must not resize
// sibling panels.
const PREVIEW_TABS = new Set(registry.camera_view?.config?.previewTabs ?? []);

export const AppShell = (): ReactElement => {
    // Every right-click menu listens for `contextmenu`, which iOS never sends.
    useEffect(() => installLongPressContextMenu(), []);
    const keyboardBox = useVisualViewportBox();

    // Measure the stage rather than the window: the rail and chat drawer both
    // consume space before panel allocation begins. Always the LIVE width — the
    // slide and the drags alike reallocate panels for the stage as it is on
    // each frame (see useStageLayout for why this is the sync guarantee).
    //
    // Measured before the panel state it feeds: the row is selected from it,
    // and an automatic open (an auto-open panel, the preview a tab expects) asks
    // whether the panel would be shown at all before adding it.
    const stageRef = useRef<HTMLDivElement>(null);
    useStageLayout(stageRef);
    const isSmViewport = useAtomValueRawSync(isSmViewportAtom);

    const openPanels = useAtomValueRawSync(openPanelsAtom);
    const mode = useAtomValueRawSync(modeAtom);
    const focusedId = useAtomValueRawSync(focusedIdAtom);
    const visibleIds = useAtomValueRawSync(visibleIdsAtom);
    const openIds = useAtomValueRawSync(openIdsAtom);
    const theme = useAtomValueRawSync(themeAtom);
    const openPanelNow = useSetAtom(openPanelAtom);
    const closePanelNow = useSetAtom(closePanelAtom);
    const closeStagePanels = useSetAtom(closeStagePanelsAtom);
    const focusPanel = useSetAtom(focusPanelAtom);
    const stepBack = useSetAtom(stepBackAtom);
    const setPanelProps = useSetAtom(setPanelPropsAtom);
    const setPanelProp = useSetAtom(setPanelPropAtom);
    const clearShellSession = useSetAtom(clearShellSessionAtom);
    const normalizeShellUrl = useSetAtom(normalizeShellUrlAtom);
    const openRestoredAutoChild = useSetAtom(openRestoredAutoChildAtom);
    const getPanelRevealedByClosing = useAtomCallback(useCallback(
        (get: Getter, _set: Setter, id: PanelId): PanelId | null => (
            get(panelRevealedByClosingAtom(id))),
        [],
    ));
    const getOpenPlanner = useAtomCallback(useCallback(
        (get: Getter): OpenPlanner => get(openPlannerAtom),
        [],
    ));

    useEffect(() => navigationGestures.track(window), []);

    // The shell can unmount and mount again on the same store, and its focus history and parked
    // set belong to one mount: the next mount starts clean, with no frame of the old parked set.
    useEffect(() => (): void => {
        clearShellSession();
    }, [clearShellSession]);

    useEffect(() => {
        normalizeShellUrl();
    }, [normalizeShellUrl]);

    // A macrotask late, because the first render's stage width is the window's; the stage
    // measures itself in a layout effect.
    useEffect(() => {
        const timer = setTimeout((): void => {
            openRestoredAutoChild();
        }, 0);
        return (): void => clearTimeout(timer);
    }, [openRestoredAutoChild]);

    useHistoryFocus();
    useHistoryGuard();

    /**
     * On a stage too narrow for the whole row, the × on the panel in front would
     * do two things at once: close it, and — because it is what anchors the mode
     * — take the workflow with it, leaving the operator on a camera tree in view
     * mode with no way back to the settings they were in. Only one of those was
     * asked for. So a close that would REVEAL the list the stage had shed, when
     * back goes to that list, steps back to it instead, and the workflow stays
     * open behind it. Otherwise — a row showing both, or a form opened from the
     * live view — the close is a close and the mode ends with it.
     *
     * Stepping back is not a departure: the panel stays mounted behind the row
     * with everything in it, so there is nothing to ask its close guard about.
     * It is parked there — see shellPanelsAtoms.ts — so it comes back only for a pick,
     * and goes when the panel it stepped back to does.
     */
    const closePanel = useCallback((panelId: PanelId): DeferredDeparture | void => {
        const stepBackTo = getPanelRevealedByClosing(panelId);
        if (stepBackTo !== null) {
            stepBack(panelId, stepBackTo);
            return undefined;
        }
        return requestPanelClose(closePanelNow, panelId, openPanels);
    }, [closePanelNow, stepBack, getPanelRevealedByClosing, openPanels]);

    // Each panel with unsaved work asks first, as for its own ×.
    const closeAll = (): void => {
        requestPanelDeparture([...openIds], { kind: 'close' }, closeStagePanels);
    };

    // Presence signal for panels that must react to a SIBLING opening or
    // closing — the lists drop their selection when the viewer they were driving
    // goes away (app/panelPresence.ts).
    usePublishOpenPanels(openPanels);

    // Live snapshot for getViewState and a declined offer — updated every render, read lazily.
    const viewRef = useLatest({ openPanels, focusedId });

    // Opening a panel can evict others (exclusions, a mode switch) or retarget this one's
    // props — either can discard unsaved edits, so the departing panels get to veto first
    // (camera_settings raises its "apply / discard / cancel" dialog). The plan is made at call
    // time, and the transition plans again, so a plan made before a dialog is never committed.
    const requestOpen = useCallback((
        request: PanelOpenRequest,
    ): ViewState | DeferredDeparture => {
        const plan = getOpenPlanner()(request);
        if (plan === null) {
            return projectViewState(viewRef.current);
        }
        const { id, props = {}, options } = request;
        return requestPanelDeparture(
            plan.departingIds,
            { kind: 'open', panelId: id, props },
            () => openPanelNow(id, props, options),
        );
    }, [getOpenPlanner, openPanelNow, viewRef]);
    // Memoized because it reaches the chat's memoized Bubbles: the ShellControl below carries it,
    // and the chat builds their onOpenPanel and onOpenCreated from that.
    const openPanel = useCallback((
        panelId: PanelId, openProps: Record<string, unknown> = {}, options?: OpenPanelOptions,
    ): ViewState | DeferredDeparture => requestOpen({
        kind: 'open', id: panelId, props: openProps, options,
    }), [requestOpen]);
    // An open that the stage declines when it cannot show the panel beside the focus.
    const openOffered = useCallback((panelId: PanelId): void => {
        requestOpen({ kind: 'open', id: panelId, isOffer: true });
    }, [requestOpen]);
    const openFromRail = useUpdateOnOpen(openPanel);

    // The shell's prop write, as the panels and the chat use it: it asks the guards first.
    const setGuardedPanelProp = useCallback((
        panelId: PanelId, key: string, value: unknown,
    ): DeferredDeparture | void => {
        const props = { [key]: value ?? null };
        return requestPanelDeparture(
            getOpenPlanner()({ kind: 'props', id: panelId, props }).departingIds,
            { kind: 'open', panelId, props },
            () => setPanelProp(panelId, key, value),
        );
    }, [getOpenPlanner, setPanelProp]);

    // A camera pick: planCameraPick decides where it lands, and this applies the plan. It returns
    // the action, so a caller can tell where the pick landed. The write goes through the guarded
    // `openPanel` above, so a pick that would evict a panel with unsaved edits still asks first.
    // While it asks, the pick returns nothing: the operator can still cancel it. The hint comes
    // before the write, so a pick that waits for the guard sets the hint as a plain pick does.
    const selectCamera = useCallback((
        cameraId: string, opts: CameraSelection,
    ): CameraPickAction | undefined => {
        const plan = planCameraPick({
            cameraId, selection: opts, openPanels, mode, visibleIds, nowMs: Date.now(),
        });
        if (plan.isJoinHintShown) {
            toast('Double-click to replace the cameras on screen, or drag one in to add it',
                { id: 'view-multi-pick' });
        } else {
            toast.dismiss('view-multi-pick');
        }
        const { write } = plan;
        if (write.kind === 'open') {
            const options = write.mode === undefined ? undefined : { mode: write.mode };
            if (isDeferred(openPanel(VIEW_PANEL, write.props, options))) {
                return undefined;
            }
        } else {
            setPanelProps(write.panelId, write.props);
        }
        if (plan.focusId !== null) {
            focusPanel(plan.focusId);
        }
        return plan.action;
    }, [openPanels, mode, visibleIds, openPanel, setPanelProps, focusPanel]);

    useEffect(() => shellCameraPick.mount(selectCamera), [selectCamera]);

    // The chat is off the stage (its config says so), and rides `openPanels`
    // only so deep links and the agent's open_panel work unchanged.
    const isChatOpen = openPanels.some((panel) => panel.id === CHAT_PANEL_ID);
    const chatProps = useMemo(
        () => openPanels.find((panel) => panel.id === CHAT_PANEL_ID)?.props ?? {},
        [openPanels],
    );
    const toggleChat = useCallback((): void => {
    // Freeze BEFORE the state change, not in the layout effect that follows
    // it. Opening the chat commits the URL flush-synchronously, so the very
    // first render of the toggle is a full reconcile of every panel — and
    // with the freeze still off, that render walks the whole objects gallery
    // (hundreds of previews) before the drawer has posed or the slots have
    // their targets. Engaging the channel first makes PanelHost reuse each
    // committed subtree in that same render, so React bails out of the panel
    // trees and the animation starts on the next frame instead of behind a
    // reconcile it never needed to do.
    //
    // useChatSlide still brackets the slide — it covers the routes that do not
    // come through this button (the drawer's own ×, the agent's open_panel, a
    // deep link) — and beginStageAnimation only ever extends a deadline, so
    // arriving here first costs nothing.
        beginStageAnimation(CHAT_SLIDE_WINDOW_MS, { panelTransition: true });
        if (isChatOpen) {
            closePanel(CHAT_PANEL_ID);
        } else {
            openPanel(CHAT_PANEL_ID);
        }
    }, [isChatOpen, closePanel, openPanel]);

    // The drawer's own × is the same action as the logo, so it takes the same
    // pre-freeze path.
    const closeFromChat = (id: PanelId): void => {
        if (id === CHAT_PANEL_ID) {
            toggleChat();
        } else {
            closePanel(id);
        }
    };

    // On a phone the chat is on the stage, so the drawer has nothing to hold.
    const {
        isMounted: isChatMounted, isShown: isChatShown, isSliding: isChatSliding,
        wrapRef: chatWrapRef, onSlideMotion,
    } = useChatSlide(isChatOpen && !isSmViewport);
    const [isShellResizing, setShellResizing] = useState(false);

    // Camera View receives the active settings tab as content context. Widths no
    // longer vary by mode or tab.
    const panelContext = useMemo((): { settingsTab?: unknown } => {
        const cameraSettings = openPanels.find((panel) => panel.id === 'camera_settings');
        return { settingsTab: cameraSettings?.props?.settingsTab };
    }, [openPanels]);

    // Auto-open camera_view when the user picks a preview-centric tab (motion,
    // line crossing). Fires on every *tab change* into a preview tab — so
    // the preview is there when the form expects it — but doesn't fight the
    // user: closing the view while staying on the same tab won't re-open it.
    const prevTabRef = useRef<unknown>(undefined);
    useEffect((): void => {
        const tab = panelContext.settingsTab;
        const prevValue = prevTabRef.current;
        if (prevValue === tab) {
            return; // no tab change — leave the user's closed state alone
        }
        prevTabRef.current = tab;

        if (typeof tab !== 'string' || !PREVIEW_TABS.has(tab)) {
            return;
        }

        const isInCameraSetup = openPanels.some((panel) => panel.id === 'camera_settings');
        const isViewAlreadyOpen = openPanels.some((panel) => panel.id === 'camera_view');
        if (!isInCameraSetup || isViewAlreadyOpen) {
            return;
        }
        // An offer: only where the stage can show it beside the form. A tab change is not
        // a request for the preview, so on a narrow stage it must not take the
        // form's place — the section header's Preview button is how the operator
        // asks for that, and it does replace the form.
        openOffered('camera_view');
    }, [panelContext.settingsTab, openPanels, openOffered]);

    const shell = useMemo((): ShellControl => ({
        openPanel,
        closePanel,
        setPanelProp: setGuardedPanelProp,
        getViewState: (): ViewState => projectViewState(viewRef.current),
        doesPanelExist: isPanelId,
        listPanels: (): PanelSummary[] => listPanels().map((config) => ({
            id: config.id,
            title: config.title,
            props: config.urlSyncedProps,
        })),
    }), [openPanel, closePanel, setGuardedPanelProp, viewRef]);

    const panelHostBindings: PanelHostBindings = {
        mode,
        theme,
        onClose: closePanel,
        openPanel,
        setPanelProp: setGuardedPanelProp,
        selectCamera,
    };
    const chatHostBindings: PanelHostBindings = {
        ...panelHostBindings,
        onClose: closeFromChat,
    };

    return (
        <TooltipProvider>
            <div
                data-keyboard={keyboardBox !== null || undefined}
                // Pinned to what the viewer sees while a keyboard is up; see
                // useVisualViewportBox. Fixed either way, so a page iOS leaves scrolled
                // once the keyboard is gone does not carry the shell off with it.
                style={keyboardBox
                    ? { top: keyboardBox.top, height: keyboardBox.height }
                    : undefined}
                className={cn(
                    // h-dvh (not h-screen): on iOS the URL bar collapse changes the visual
                    // viewport; 100dvh tracks it while 100vh leaves a dead strip.
                    'fixed inset-x-0 top-0 flex h-dvh flex-col',
                    'bg-occ-background-main text-occ-foreground-main',
                    'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]',
                    // A long press is the app's (installLongPressContextMenu), not the
                    // browser's save-image or link-preview callout.
                    'pointer-coarse:[-webkit-touch-callout:none]',
                )}
            >
                <ShellControlContext.Provider value={shell}>
                    <PortalUnavailableBanner />
                    <ContractViolationBanner />

                    {/* Everything the banners sit above. `min-h-0` is what lets it give
              up the banners' height instead of overflowing the viewport. */}
                    <div className="relative flex min-h-0 flex-1">
                        {/* Spans the shell below the banners. The rail and PanelStage's
                <main> are both `relative`, so they paint above it without
                needing a z-index. */}
                        {/* A faint dot grid. Dark surfaces need louder dots to register. */}
                        <div
                            aria-hidden="true"
                            className={cn('pointer-events-none absolute inset-0',
                                'opacity-10 dark:opacity-15 bg-size-[16px_16px] bg-radial',
                                'from-occ-foreground-high-contrast from-[0.5px]',
                                'to-transparent to-[1px]')}
                        />

                        {isChatMounted && !isSmViewport && (
                            <ChatDrawer
                                open={isChatShown}
                                wrapRef={chatWrapRef}
                                panelProps={chatProps}
                                bindings={chatHostBindings}
                                onResizeChange={setShellResizing}
                                onSlideMotion={onSlideMotion}
                            />
                        )}

                        {!isSmViewport && (
                            <NavRail
                                openPanel={openFromRail}
                                onCloseAll={closeAll}
                                // The drawer's POSE, so the rail's logo row collapses and reopens
                                // on the same 200ms as the slide — the two read as one movement.
                                // (Not `isChatMounted`, which lags the close by the exit's length
                                // and would leave the buttons to move after the drawer had gone.)
                                reserveLogoSpace={!isChatShown}
                            />
                        )}

                        <div
                            ref={stageRef}
                            className="relative min-w-0 flex-1"
                        >
                            {/* Lives here, not on the rail, so it sits half a gap before the
                    first panel — the same offset every panel-to-panel splitter
                    has from the panel on its right. */}
                            {!isSmViewport && (
                                <NavResizeHandle onResizeChange={setShellResizing} />
                            )}
                            {isSmViewport && visibleIds.size === 0 ? (
                                <PhoneNav openPanel={openFromRail} />
                            ) : (
                                <PanelStage
                                    bindings={panelHostBindings}
                                    // The stage mounts it exactly while it has nothing else to
                                    // draw, which is what makes the launcher appear and go on
                                    // its own.
                                    empty={(
                                        <HomeLauncher openPanel={openFromRail} />
                                    )}
                                    // No header bar here, so panels need the same 16px of
                                    // padding at the top that --pad already gives them on the
                                    // other three edges.
                                    isInsetTop
                                    context={panelContext}
                                    isLiveResize={isShellResizing || isChatSliding}
                                />
                            )}
                        </div>

                        {/* The logo owns the window's top-left corner regardless of what is
                  beneath it — rail when the chat is closed, drawer when it's open —
                  so its offset never moves. It is the chat's only control; the
                  drawer's × is the same action. */}
                        {!isSmViewport && (
                            <Logo
                                open={isChatOpen}
                                onClick={toggleChat}
                            />
                        )}
                    </div>
                </ShellControlContext.Provider>
            </div>
        </TooltipProvider>
    );
};
